import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Copy, RefreshCw, Play, Radio, Settings, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents } from '@/lib/mockData';

export function AdminStreamConfig() {
  const { eventId } = useParams();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];

  const [config, setConfig] = useState({
    streamName: event.title + ' - Live Stream',
    streamKey: 'sk_live_' + Math.random().toString(36).substring(2, 15),
    rtmpUrl: 'rtmp://live.exit88.com/stream',
    dvrDuration: '2',
    lowLatency: true,
    recordStream: true,
    chatEnabled: true
  });

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleRegenerateKey = () => {
    setConfig({
      ...config,
      streamKey: 'sk_live_' + Math.random().toString(36).substring(2, 15)
    });
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <Link to={`/admin/events/${eventId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Event
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
            <Radio className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Stream Configuration</h1>
            <p className="text-muted-foreground">{event.title}</p>
          </div>
        </div>

        {/* Stream Status */}
        <Card className="bg-card border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-white font-medium">Stream Status</span>
            </div>
            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 text-sm font-semibold rounded-full">
              Ready to Stream
            </span>
          </div>
        </Card>

        {/* Stream Credentials */}
        <Card className="bg-card border-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Stream Credentials
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Stream Name</label>
              <Input 
                value={config.streamName}
                onChange={(e) => setConfig({...config, streamName: e.target.value})}
                className="bg-secondary border-border"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">RTMP URL</label>
              <div className="flex gap-2">
                <Input 
                  value={config.rtmpUrl}
                  readOnly
                  className="bg-secondary border-border font-mono text-sm"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="border-border"
                  onClick={() => handleCopy(config.rtmpUrl)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Stream Key</label>
              <div className="flex gap-2">
                <Input 
                  type="password"
                  value={config.streamKey}
                  readOnly
                  className="bg-secondary border-border font-mono text-sm"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="border-border"
                  onClick={() => handleCopy(config.streamKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="border-border"
                  onClick={handleRegenerateKey}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Keep this key secret. It allows anyone to stream to your channel.</p>
            </div>
          </div>
        </Card>

        {/* DVR Settings */}
        <Card className="bg-card border-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            DVR Settings
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">DVR Window Duration</label>
              <Select value={config.dvrDuration} onValueChange={(value) => setConfig({...config, dvrDuration: value})}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Disabled</SelectItem>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Allow viewers to rewind and catch up on the stream.</p>
            </div>
          </div>
        </Card>

        {/* Stream Options */}
        <Card className="bg-card border-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Stream Options</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Low Latency Mode</p>
                <p className="text-sm text-muted-foreground">Reduce stream delay for better interactivity</p>
              </div>
              <Switch 
                checked={config.lowLatency}
                onCheckedChange={(checked) => setConfig({...config, lowLatency: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Record Stream</p>
                <p className="text-sm text-muted-foreground">Save a copy of the stream for later viewing</p>
              </div>
              <Switch 
                checked={config.recordStream}
                onCheckedChange={(checked) => setConfig({...config, recordStream: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Enable Chat</p>
                <p className="text-sm text-muted-foreground">Allow viewers to chat during the stream</p>
              </div>
              <Switch 
                checked={config.chatEnabled}
                onCheckedChange={(checked) => setConfig({...config, chatEnabled: checked})}
              />
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Link to={`/admin/events/${eventId}/stream/monitor`} className="flex-1">
            <Button variant="outline" className="w-full border-border">
              <Play className="w-4 h-4 mr-2" />
              Go to Stream Monitor
            </Button>
          </Link>
          <Button className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
            Save Configuration
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminStreamConfig;
